import {StorageService} from '../../services/storage/storage.service';
import {IRecordOption} from '../../interfaces/record';
import {cloneDeep} from 'lodash';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {IService} from './service.interface';
import {IClinicalObjective} from '../clinic';
import {INoteTimestamp} from './note-timestamp.interface';
import {AppointmentActions, AppointmentState, IAppointment} from '../appointment';
import {IServiceAppointments} from './service-appointments.interface';
import {IServicesByPatient} from './services-by-patient.interface';
import {firstValueFrom, Observable} from 'rxjs';
import {RecordTypeEnum} from 'src/app/enums/record/record-type.enum';
import {ExpertAction} from '../expert';
import {SphereModeEnum} from '../../enums/sphere/sphere-mode.enum';

export interface IRService {
  activeService: IService;
  clinicalObjectives: IClinicalObjective[];
  appointmentsCurrentService: IAppointment[];
  lastAppointment: IAppointment;
  oldServices: IService[];
  selectedService?: string;
  currentNoteCreated?: INoteTimestamp;

  selectedAppointmentIdForNotes: string;
  notesFromAppointmentSelected?: INoteTimestamp[];
  countNotes: number;
  countNotesByService: number;
  notesFromService: INoteTimestamp[];
  servicesByPatient: IServicesByPatient[];

  recordsInformation?: IRecordOption[];
}

export namespace ServiceAction {
  export class GetActive {
    static readonly type = '[SERVICES] Get Active Service Action';

    constructor(public patientId: string, public expertId: string) {
    }
  }

  export class Post {
    static readonly type = '[SERVICES] Post Service Action';

    constructor(public patientId: string, public copyPastRecord: boolean = false) {
    }
  }

  export class PatchClinicalObjectives {
    static readonly type = '[SERVICES] Patch Clinical Objectives Service Action';

    constructor(public serviceId: string, public patientId: string, public clinicalObjectives: string[]) {
    }
  }

  export class PostNote {
    static readonly type = '[SERVICES] Post Note To Service Action';

    constructor(public serviceId: string, public patientId: string, public note: INoteTimestamp, public isCurrentService?: boolean) {
    }
  }

  export class PostNoteAppointment {
    static readonly type = '[SERVICES] Post Note To Appointment Action';

    constructor(public appointmentId: string, public patientId: string, public note: INoteTimestamp, public isCurrentService?: boolean) {
    }
  }

  export class PatchAppointmentNote {
    static readonly type = '[SERVICES] Patch Note Action';

    constructor(
      public noteTimeStampId: string,
      public patientCanView: boolean,
      public type: 'appointmentNotes' | 'serviceNotes',
      public note?: string,
    ) {
    }
  }

  export class DeleteAppointmentNoteById {
    static readonly type = '[SERVICES] Delete Note Action';

    constructor(public noteTimeStampId: string) {
    }
  }

  export class PatchLocal {
    static readonly type = '[SERVICES] Set Properties To Service Action';

    constructor(public properties: Partial<IService>) {
    }
  }

  export class GetAppointmentsByService {
    static readonly type = '[SERVICES] Get Appointments By Services Action';

    constructor(public patientId: string, public expertId: string) {
    }
  }

  export class CompleteAppointment {
    static readonly type = '[SERVICES] Complete Appointment Action';

    constructor(public appointmentId: string, public duration: number) {
    }
  }

  export class AddAppointment {
    static readonly type = '[SERVICES] Add Appointment To Active Service Action';

    constructor(public appointment: IAppointment) {
    }
  }

  export class AddAppointmentNote {
    static readonly type = '[SERVICES] Add Note To Appointment Action';

    constructor(public appointmentId: string, public patientId: string, public note: INoteTimestamp) {
    }
  }

  export class ResetState {
    static readonly type = '[SERVICES] Reset Data Service Action';

    constructor() {
    }
  }


  export class GetAppointmentNotes {
    static readonly type = '[SERVICES] Get Notes By Appointment Id Action';

    constructor(
      public appointmentId: string,
      public patientUserId: string,
      public search: string,
      public showNotes: string,
      public filters?: { params: { skip: number, limit: number } }
    ) {
    }
  }

  export class CountAppointmentNotes {
    static readonly type = '[SERVICES] Get Notes Count Action';

    constructor(
      public appointmentId: string,
      public patientUserId: string,
      public search: string,
      public showNotes: string,
      public filters?: { params: { skip: number, limit: number } }
    ) {
    }
  }

  export class GetNotesAllAppointments {
    static readonly type = '[SERVICES] Get Notes All Appointments Action';

    constructor(
      public serviceId: string,
      public patientUserId: string,
      public search: string,
      public showNotes: string,
      public filters?: { params: { skip: number, limit: number } }
    ) {
    }
  }

  export class CountNotesAllAppointments {
    static readonly type = '[SERVICES] Get Notes All Appointments Count Action';

    constructor(
      public serviceId: string,
      public patientUserId: string,
      public search: string,
      public showNotes: string,
      public filters?: { params: { skip: number, limit: number } }
    ) {
    }
  }

  export class GetNotes {
    static readonly type = '[SERVICES] Get Notes By Service Action';

    constructor(
      public serviceId: string,
      public patientUserId: string,
      public search: string,
      public showNotes: string,
      public filters?: { params: { skip: number, limit: number } }) {
    }
  }

  export class CountNotes {
    static readonly type = '[SERVICES] Get Notes By Service Count Action';

    constructor(
      public serviceId: string,
      public patientUserId: string,
      public search: string = '',
      public showNotes: string, public filters?: { params: { skip: number, limit: number } }) {
    }
  }

  export class SetAppointmentIdForNotes {
    static readonly type = '[SERVICES] Set Appointment Id Selected For Notes Action';

    constructor(public value: string) {
    }
  }

  export class SetNotesAction {
    static readonly type = '[SERVICES] Set Notes Action';

    constructor(public notes: INoteTimestamp[]) {
    }
  }

  export class GetExpertServices {
    static readonly type = '[SERVICES] Get Service Experts Action';

    constructor(public search: string = '', public filters?: { params: { skip: number, limit: number } }) {
    }
  }

  export class CountExpertServices {
    static readonly type = '[SERVICES] Get Service Experts Count Action';

    constructor(public search: string = '', public filters?: { params: { skip: number, limit: number } }) {
    }
  }

  export class GetExpertServicesByPatientId {
    static readonly type = '[SERVICES] Get Services By Patient Action';

    constructor(public patientId: string) {
    }
  }

  export class AssignNoteToAppointment {
    static readonly type = '[SERVICES] Assign Note To Appointment Action';

    constructor(public noteTimestampId: string, public appointmentId: string) {
    }
  }

  export class AssignNoteToService {
    static readonly type: string = '[SERVICES] Assign Note To Service Action';

    constructor(public noteTimestampId: string, public serviceId: string, public patientId: string) {
    }
  }

  export class GetRecords {
    static readonly type: string = '[SERVICES] Get Records Information Action';

    constructor(public serviceId: string, public patientId: string, public mode?: SphereModeEnum) {
    }
  }

  export class SwitchBetweenSphereAndMiniSphere {
    static readonly type: string = '[SERVICES] Switch Between Sphere And MiniSphere Action';

    constructor(public serviceId: string, public patientId: string, public mode: SphereModeEnum) {
    }
  }
}

@State<IRService>({
  name: 'serviceState',
  defaults: {
    activeService: null,
    clinicalObjectives: [],
    lastAppointment: null,
    appointmentsCurrentService: [],
    oldServices: [],
    selectedService: null,

    selectedAppointmentIdForNotes: null,
    notesFromAppointmentSelected: [],
    countNotes: 0,
    countNotesByService: 0,
    notesFromService: [],
    servicesByPatient: []
  },
})

@Injectable()
export class ServiceState {
  constructor(
    private baseService: BaseService<any>,
    private store: Store,
    private storageService: StorageService
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getActive(state: IRService): IService {
    return state.activeService;
  }

  @Selector()
  public static getServiceSelectedId(state: IRService): string {
    return state.selectedService;
  }

  @Selector()
  public static getLastAppointment(state: IRService): IAppointment {
    return state.lastAppointment;
  }

  @Selector()
  public static getCurrentAppointments(state: IRService): IAppointment[] {
    return state.appointmentsCurrentService;
  }

  @Selector()
  public static getOldServices(state: IRService): IService[] {
    return state.oldServices;
  }

  @Selector()
  public static getCurrentNoteCreated(state: IRService): INoteTimestamp {
    return state.currentNoteCreated;
  }

  @Selector()
  static getSelectedIdAppointmentForNotes(state: IRService): any { // TODO: Revisar interfaz respecto a donde se está usando
    return state.selectedAppointmentIdForNotes;
  }

  @Selector()
  static getNotes(state: IRService): any[] { // TODO: Revisar interfaz respecto a donde se está usando
    return state.notesFromAppointmentSelected;
  }

  @Selector()
  static getNotesCount(state: IRService): number {
    return state.countNotes;
  }

  @Selector()
  static getNotesByServiceCount(state: IRService): number {
    return state.countNotesByService;
  }

  @Selector()
  static getNotesByService(state: IRService): any[] { // TODO: Revisar interfaz respecto a donde se está usando
    return state.notesFromService;
  }

  @Selector()
  static getServicesByPatient(state: IRService): IServicesByPatient[] {
    return state.servicesByPatient;
  }

  @Selector()
  static getRecordsInformation(state: IRService): IRecordOption[] {
    return state.recordsInformation;
  }

  @Selector()
  static getServicePatientRecordMode(state: IRService): SphereModeEnum {
    return state.activeService.servicePatients[0].recordMode;
  }

  /**
   * Le coloca un enum específico para cada ficha
   */
  setUIDRecords(records: IRecordOption[]): IRecordOption[] {
    // tslint:disable-next-line:variable-name
    records = records.map(_record => {
      if (_record.name === 'Hábitos generales') {
        _record.uid = RecordTypeEnum.HABITS;
      } else if (_record.name === 'Historial de actividad física') {
        _record.uid = RecordTypeEnum.PHYSICAL_ACTIVITY;
      } else if (_record.name === 'Historial médico personal') {
        _record.uid = RecordTypeEnum.PERSONAL_MEDIC;
      } else if (_record.name === 'Historial médico heredo-familiar') {
        _record.uid = RecordTypeEnum.FAMILIAL_MEDICAL;
      } else if (_record.name === 'Historial nutricional') {
        _record.uid = RecordTypeEnum.NUTRITIONAL;
      } else if (_record.name === 'Historial de actitud') {
        _record.uid = RecordTypeEnum.ATTITUDE;
      } else if (_record.name === 'Historial de afectividad y entorno') {
        _record.uid = RecordTypeEnum.ENVIRONMENT_AFFECTIVITY;
      } else if (_record.name === 'Historial de exploración física nutricional') {
        _record.uid = RecordTypeEnum.NUTRITIONAL_PHYSICAL_EXAMINATION;
      } else if (_record.name === 'Historial de sueño') {
        _record.uid = RecordTypeEnum.SLEEP;
      } else if (_record.name === 'Historial bioquímico') {
        _record.uid = RecordTypeEnum.BIOCHEMISTS;
      } else if (_record.name === 'Mi experiencia') {
        _record.uid = RecordTypeEnum.MY_EXPERIENCE;
      } else if (_record.name === 'Mini ficha') {
        _record.uid = RecordTypeEnum.MINI_SPHERE;
      }
      return _record;
    });
    return records;
  }

  @Action(ServiceAction.GetActive)
  async getActiveServiceAction(ctx: StateContext<IRService>, action: ServiceAction.GetActive): Promise<void> {
    const response: DataBaseServiceResponse<IService> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/${action.patientId}/${action.expertId}/active`)
    );
    if (response.error) {
      ctx.patchState({
        activeService: null
      });
      throw new Error(response.message);
    }

    ctx.patchState({
      activeService: response.entity,
      selectedService: response.entity._id
    });
  }


  @Action(ServiceAction.Post)
  async postServiceAction(ctx: StateContext<IRService>, action: ServiceAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IService> = await firstValueFrom(
      this.baseService.post(`${this.url}/services/${action.patientId}`, {copyPastRecord: action.copyPastRecord})
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      activeService: response.entity,
      selectedService: response.entity._id
    });
  }

  @Action(ServiceAction.PatchClinicalObjectives)
  async patchClinicalObjectivesServiceAction(ctx: StateContext<IRService>, action: ServiceAction.PatchClinicalObjectives): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/services/${action.serviceId}/patients/${action.patientId}/service-patients/clinic-objectives`,
        {objectives: action.clinicalObjectives}
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(ServiceAction.PatchLocal)
  async setPropertiesToServiceAction(ctx: StateContext<IRService>, action: ServiceAction.PatchLocal): Promise<void> {
    const service = cloneDeep(ctx.getState().activeService);
    ctx.patchState({
      activeService: {
        ...service,
        ...action.properties
      }
    });
  }

  @Action(ServiceAction.GetExpertServices)
  async getServiceExpertsAction(ctx: StateContext<IRService>, action: ServiceAction.GetExpertServices): Promise<void> {
    const response = await firstValueFrom(
      this.baseService.get(
        `${this.url}/services/experts?search=${action.search}`,
        void 0,
        void 0,
        action.filters
      )
    );

    if (response.error) throw new Error(response.message);

    await firstValueFrom(this.store.dispatch(new ExpertAction.SetExperts(response.entity)));
  }

  @Action(ServiceAction.CountExpertServices)
  async getServiceExpertsCountAction(ctx: StateContext<IRService>, action: ServiceAction.CountExpertServices): Promise<void> {
    const response = await firstValueFrom(
      this.baseService.get(
        `${this.url}/services/experts/count`,
        void 0,
        void 0,
        {params: {search: action.search}}
      )
    );
    if (response.error) throw new Error(response.message);

    await firstValueFrom(this.store.dispatch(new ExpertAction.SetCount(response.entity.count ?? 0)));
  }

  @Action(ServiceAction.GetExpertServicesByPatientId)
  async getServicesByPatientAction(ctx: StateContext<IRService>, {patientId}: ServiceAction.GetExpertServicesByPatientId): Promise<void> {
    const response: DataBaseServiceResponse<IServicesByPatient[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/patients/${patientId}/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      servicesByPatient: response.entity
    });
  }

  @Action(ServiceAction.GetRecords)
  async getRecordsInformationAction(ctx: StateContext<IRService>, action: ServiceAction.GetRecords): Promise<void> {
    const response: DataBaseServiceResponse<IRecordOption[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/latest-updates`, void 0, void 0, {
        params: {
          mode: action.mode
        }
      })
    );

    if (response.error) throw new Error(response.message);

    let records: IRecordOption[] = response.entity;
    records = this.setUIDRecords(records);

    ctx.patchState({
      recordsInformation: records
    });
  }

  @Action(ServiceAction.SwitchBetweenSphereAndMiniSphere)
  async switchBetweenSphereAndMiniSphere(
    ctx: StateContext<IRService>,
    {serviceId, patientId, mode}: ServiceAction.SwitchBetweenSphereAndMiniSphere
  ): Promise<void> {
    const request: Observable<DataBaseServiceResponse<void>> = this.baseService.patch(
      `${this.url}/patients/${patientId}/service/${serviceId}/records/switch`,
      {},
      void 0,
      void 0,
      {params: {mode}}
    );
    const response: DataBaseServiceResponse<void> = await firstValueFrom(request);

    if (response.error) throw new Error(response.message);
    const activeService = cloneDeep(ctx.getState().activeService);
    // tslint:disable-next-line:no-unused-expression
    activeService.servicePatients[0].recordMode = mode;
    ctx.patchState({
      activeService
    });
  }


  /* *************************************************************************************** */
  /* **************************** SERVICE ************************************************** */
  /* *************************************************************************************** */


  @Action(ServiceAction.PostNote)
  async postNoteToServiceAction(
    ctx: StateContext<IRService>,
    {serviceId, patientId, note, isCurrentService}: ServiceAction.PostNote
  ): Promise<void> {
    const response: DataBaseServiceResponse<INoteTimestamp> = await firstValueFrom(
      this.baseService.post(`${this.url}/services/${serviceId}/patients/${patientId}/service-patients/notes`, note)
    );

    if (response.error) throw new Error(response.message);

    if (isCurrentService) {
      const appointments = cloneDeep(ctx.getState().appointmentsCurrentService);
      appointments.forEach(appointment => {
        appointment.users.forEach(user => {
          if (user.hasOwnProperty('noteCount')) {
            user.noteCount += 1;
          } else {
            user.noteCount = 1;
          }
        });
      });
      ctx.patchState({
        appointmentsCurrentService: [...appointments],
      });
    } else {
      const services = cloneDeep(ctx.getState().oldServices);
      if (services && services.length > 0) {
        const serviceIndex = services.findIndex(service => service._id === serviceId);
        if (serviceIndex !== -1) {
          services[serviceIndex].appointments.forEach(appointment => {
            appointment.users.forEach(user => {
              if (user.hasOwnProperty('noteCount')) {
                user.noteCount += 1;
              } else {
                user.noteCount = 1;
              }
            });
          });
        }
      }
      ctx.patchState({
        oldServices: [...services],
      });
    }

    let notesFromService: INoteTimestamp[] = cloneDeep(ctx.getState().notesFromService);
    if (Array.isArray(notesFromService)) {
      notesFromService.unshift(response.entity);
    } else {
      notesFromService = [response.entity];
    }

    if (notesFromService.length > 10) {
      notesFromService.pop();
    }

    ctx.patchState({
      currentNoteCreated: response.entity,
      notesFromService: [...notesFromService]
    });
  }

  @Action(ServiceAction.GetNotes)
  async getNotesByServiceAction(ctx: StateContext<IRService>, action: ServiceAction.GetNotes): Promise<void> {
    const response: DataBaseServiceResponse<INoteTimestamp[]> = await firstValueFrom(
      this.baseService.get(
        `${this.url}/services/${action.serviceId}/patients/${action.patientUserId}/service-patients/notes`,
        void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      notesFromService: response.entity
    });
  }

  @Action(ServiceAction.CountNotes)
  async getNotesByServiceCountAction(ctx: StateContext<IRService>, action: ServiceAction.CountNotes): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(
        `${this.url}/services/${action.serviceId}/patients/${action.patientUserId}/service-patients/notes/count`,
        void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      countNotesByService: response.entity.count ?? 0,
    });
  }


  @Action(ServiceAction.AssignNoteToService)
  async assignNoteToServiceAction(ctx: StateContext<IRService>, action: ServiceAction.AssignNoteToService): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/notes/${action.noteTimestampId}/move/service/${action.serviceId}/patient/${action.patientId}`, void 0)
    );

    if (response.error) throw new Error(response.message);

    let notesFromService = cloneDeep(ctx.getState().notesFromService);
    const notesFromAppointment = cloneDeep(ctx.getState().notesFromAppointmentSelected);
    const noteIndex = notesFromAppointment.findIndex(n => n._id === action.noteTimestampId);
    const note = notesFromAppointment[noteIndex];

    if (Array.isArray(notesFromService)) {
      notesFromService.unshift(note);
    } else {
      notesFromService = [note];
    }

    notesFromAppointment.splice(noteIndex, 1);

    if (noteIndex !== -1) {
      ctx.patchState({
        notesFromAppointmentSelected: notesFromAppointment,
        notesFromService
      });
    }
  }


  /* *************************************************************************************** */
  /* ********************************* APPOINTMENT ***************************************** */
  /* *************************************************************************************** */


  @Action(ServiceAction.CompleteAppointment)
  async completeAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.CompleteAppointment): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.post(`${this.url}/appointments/${action.appointmentId}/completed`, {duration: action.duration}, void 0)
    );

    if (response.error) throw new Error(response.message);

    this.storageService.setLocalItem('durationAppointment', null);
    const timesSessions: { date: Date; appointmentId: string }[] = this.storageService.getSessionItem<{
      date: Date;
      appointmentId: string
    }[]>('consultationStart') ?? [];
    const timeByAppointmentIndex = timesSessions.findIndex((item) => item.appointmentId === action.appointmentId);
    if (timeByAppointmentIndex !== -1) {
      timesSessions.splice(timeByAppointmentIndex, 1);
      this.storageService.setSessionItem('consultationStart', timesSessions);
    }

    await firstValueFrom(this.store.dispatch(new AppointmentActions.UpdateAppointmentAsCompleted(action.appointmentId)));
  }

  @Action(ServiceAction.GetAppointmentsByService)
  async getAppointmentsByServicesAction(ctx: StateContext<IRService>, action: ServiceAction.GetAppointmentsByService): Promise<void> {
    const response: DataBaseServiceResponse<IServiceAppointments> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/patients/${action.patientId}/experts/${action.expertId}/appointments`, void 0, void 0)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      appointmentsCurrentService: response.entity.currentService.appointments,
      oldServices: response.entity.oldServices,
      lastAppointment: response.entity.lastAppointment,
      activeService: {...ctx.getState().activeService, ...response.entity.currentService},
      selectedService: response.entity.currentService._id
    });
  }

  @Action(ServiceAction.PostNoteAppointment)
  async postNoteToAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.PostNoteAppointment): Promise<void> {
    const response: DataBaseServiceResponse<INoteTimestamp> = await firstValueFrom(
      this.baseService.post(`${this.url}/appointments/${action.appointmentId}/${action.patientId}/note`, action.note)
    );

    if (response.error) throw new Error(response.message);

    const appointments = cloneDeep(ctx.getState().appointmentsCurrentService);
    if (action.isCurrentService) {
      const appointmentIndex = appointments.findIndex(appointment => appointment._id === action.appointmentId);
      if (appointmentIndex !== -1) {
        appointments[appointmentIndex].users.forEach(user => {
          if (user.hasOwnProperty('noteCount')) {
            user.noteCount += 1;
          } else {
            user.noteCount = 1;
          }
        });
        ctx.patchState({
          appointmentsCurrentService: [...appointments]
        });
      }
    } else {
      const services = cloneDeep(ctx.getState().oldServices);
      const serviceIndex = services.findIndex(service => service.appointments.find(
        appointment => appointment._id === action.appointmentId));
      if (serviceIndex !== -1) {
        const appointmentIndex = services[serviceIndex].appointments.findIndex(appointment => appointment._id === action.appointmentId);
        if (appointmentIndex !== -1) {
          services[serviceIndex].appointments[appointmentIndex].users.forEach(user => {
            if (user.hasOwnProperty('noteCount')) {
              user.noteCount += 1;
            } else {
              user.noteCount = 1;
            }
          });
          ctx.patchState({
            oldServices: [...services]
          });
        }
      }
    }

    // aumentar el contador
    let notesFromAppointment = cloneDeep(ctx.getState().notesFromAppointmentSelected);
    if (Array.isArray(notesFromAppointment)) {
      notesFromAppointment.unshift(response.entity);
    } else {
      notesFromAppointment = [response.entity];
    }
    if (notesFromAppointment.length > 10) {
      notesFromAppointment.pop();
    }

    ctx.patchState({
      currentNoteCreated: response.entity,
      notesFromAppointmentSelected: [...notesFromAppointment]
    });
  }


  @Action(ServiceAction.GetAppointmentNotes)
  async getNotesByAppointmentIdAction(ctx: StateContext<IRService>, action: ServiceAction.GetAppointmentNotes): Promise<void> {
    const response: DataBaseServiceResponse<INoteTimestamp[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/notes/appointments/${action.appointmentId}/patients/${action.patientUserId}`,
      void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      selectedAppointmentIdForNotes: action.appointmentId,
      notesFromAppointmentSelected: response.entity
    });
  }

  @Action(ServiceAction.CountAppointmentNotes)
  async getNotesCountAction(ctx: StateContext<IRService>, action: ServiceAction.CountAppointmentNotes): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(
        `${this.url}/notes/appointments/${action.appointmentId}/patients/${action.patientUserId}/count`,
        void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      countNotes: response.entity.count ?? 0,
    });
  }

  @Action(ServiceAction.CountNotesAllAppointments)
  async getNotesAllAppointmentsCountAction(ctx: StateContext<IRService>, action: ServiceAction.CountNotesAllAppointments): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(
        `${this.url}/notes/services/${action.serviceId}/patients/${action.patientUserId}/appointments/count`,
        void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      countNotes: response.entity.count ?? 0,
    });
  }

  @Action(ServiceAction.GetNotesAllAppointments)
  async getNotesAllAppointmentsAction(ctx: StateContext<IRService>, action: ServiceAction.GetNotesAllAppointments): Promise<void> {
    const response = await firstValueFrom(
      this.baseService.get(
        `${this.url}/notes/services/${action.serviceId}/patients/${action.patientUserId}/appointments`,
        void 0,
        void 0,
        {params: {...action.filters.params, search: action.search, showNotes: action.showNotes}}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      notesFromAppointmentSelected: response.entity
    });
  }

  @Action(ServiceAction.SetAppointmentIdForNotes)
  async setAppointmentIdSelectedForNotesAction(ctx: StateContext<IRService>, action: ServiceAction.SetAppointmentIdForNotes): Promise<void> {
    ctx.patchState({
      selectedAppointmentIdForNotes: action.value
    });
  }

  @Action(ServiceAction.SetNotesAction)
  async setNotesAction(ctx: StateContext<IRService>, action: ServiceAction.SetNotesAction): Promise<void> {
    ctx.patchState({
      notesFromAppointmentSelected: action.notes
    });
  }

  @Action(ServiceAction.PatchAppointmentNote)
  async patchNoteToAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.PatchAppointmentNote): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/notes/noteTimestamps/${action.noteTimeStampId}`, {patientCanView: action.patientCanView})
    );

    if (response.error) throw new Error(response.message);

    if (action.type === 'appointmentNotes') {
      const noteTimestamps = cloneDeep(ctx.getState().notesFromAppointmentSelected);
      const index = noteTimestamps.findIndex((noteTimestamp: INoteTimestamp) => noteTimestamp._id === action.noteTimeStampId);
      if (index !== -1) {
        noteTimestamps[index].patientCanView = action.patientCanView;
        ctx.patchState({notesFromAppointmentSelected: [...noteTimestamps]});
      }
      return;
    }

    if (action.type === 'serviceNotes') {
      const noteTimestamps = cloneDeep(ctx.getState().notesFromService);
      const index = noteTimestamps.findIndex((noteTimestamp: INoteTimestamp) => noteTimestamp._id === action.noteTimeStampId);
      if (index !== -1) {
        noteTimestamps[index].patientCanView = action.patientCanView;
        ctx.patchState({notesFromService: [...noteTimestamps]});
      }
      return;
    }
  }

  @Action(ServiceAction.AddAppointment)
  async addAppointmentToActiveServiceAction(ctx: StateContext<IRService>, action: ServiceAction.AddAppointment): Promise<void> {
    if (Array.isArray(ctx.getState().appointmentsCurrentService)) {
      ctx.patchState({
        appointmentsCurrentService: [...ctx.getState().appointmentsCurrentService, action.appointment]
      });
    } else {
      ctx.patchState({
        appointmentsCurrentService: [action.appointment]
      });
    }
  }

  @Action(ServiceAction.AddAppointmentNote)
  async addNoteToAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.AddAppointmentNote): Promise<void> {
    const currentAppointments = cloneDeep(ctx.getState().appointmentsCurrentService);
    const indexAppointment = currentAppointments.findIndex(appointment => appointment._id === action.appointmentId);
    if (indexAppointment !== -1) {
      const indexUser = currentAppointments[indexAppointment].users.findIndex(user => user.patientId === action.patientId);
      if (indexUser !== -1) {
        currentAppointments[indexAppointment].users[indexUser].note.noteTimestamps.push(action.note);
      }
    } else {
      // buscar en los appointments pasados
    }
  }

  @Action(ServiceAction.AssignNoteToAppointment)
  async assignNoteToAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.AssignNoteToAppointment): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/notes/${action.noteTimestampId}/move/appointment/${action.appointmentId}`, void 0)
    );

    if (response.error) throw new Error(response.message);

    const notesFromService = cloneDeep(ctx.getState().notesFromService);
    let notesFromAppointment = cloneDeep(ctx.getState().notesFromAppointmentSelected);
    const noteIndex = notesFromService.findIndex(n => n._id === action.noteTimestampId);
    const note = notesFromService[noteIndex];
    if (Array.isArray(notesFromAppointment)) {
      notesFromAppointment.unshift(note);
    } else {
      notesFromAppointment = [note];
    }
    notesFromService.splice(noteIndex, 1);

    if (noteIndex !== -1) {
      ctx.patchState({
        notesFromAppointmentSelected: notesFromAppointment,
        notesFromService
      });
    }
  }

  @Action(ServiceAction.DeleteAppointmentNoteById)
  async deleteNoteToAppointmentAction(ctx: StateContext<IRService>, action: ServiceAction.DeleteAppointmentNoteById): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/notes/noteTimestamps/${action.noteTimeStampId}`)
    );

    if (response.error) throw new Error(response.message);
  }


  @Action(ServiceAction.ResetState)
  async resetDataServiceAction(ctx: StateContext<IRService>, _: ServiceAction.ResetState): Promise<void> {
    ctx.patchState({
      appointmentsCurrentService: [],
      oldServices: [],
      currentNoteCreated: null,
      lastAppointment: null,
      clinicalObjectives: [],
      activeService: null,
      selectedService: null,
      selectedAppointmentIdForNotes: null,
      notesFromAppointmentSelected: [],
      countNotes: 0,
      countNotesByService: 0,
      notesFromService: [],
      servicesByPatient: []
    });
  }

}

